import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  HomeIcon,
  CurrencyDollarIcon,
  PlusIcon,
  BanknotesIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import INRInvestorForm from "../components/INRInvestorForm";
import INRInvestorsTable from "../components/INRInvestorsTable";
import INRBulkUpload from "../components/INRBulkUpload";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import {
  fetchINRInvestors,
  addINRInvestor,
  updateINRInvestor,
  deleteINRInvestor,
  softDeleteINRInvestor,
} from "../services/inrInvestorsService";

const PAGE_SIZE = 10;

const INRInvestorsPage = () => {
  const [investors, setInvestors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [stats, setStats] = useState({
    totalInvestors: 0,
    totalReceived: 0,
    totalSent: 0,
    totalBalance: 0,
  });

  const loadInvestors = async (opts = {}) => {
    const isSearchOperation = opts.hasOwnProperty("search");
    const isInitialLoad = !isSearchOperation && !opts.hasOwnProperty("page");

    if (isSearchOperation) {
      setSearchLoading(true);
    } else if (isInitialLoad) {
      setLoading(true);
    }
    // Don't show main loading for pagination

    setError(null);
    try {
      console.log("Loading INR investors with options:", opts);
      const result = await fetchINRInvestors({
        search: opts.search !== undefined ? opts.search : search,
        page: opts.page || page,
        limit: PAGE_SIZE,
      });
      console.log("INR investors API response:", result);

      const { rows, total } = result;
      setInvestors(rows);
      setTotal(total);

      // Calculate statistics
      const totalReceived = rows.reduce(
        (sum, inv) => sum + (parseFloat(inv.received) || 0),
        0
      );
      const totalSent = rows.reduce(
        (sum, inv) => sum + (parseFloat(inv.sent) || 0),
        0
      );
      const totalBalance = rows.reduce(
        (sum, inv) => sum + (parseFloat(inv.balance) || 0),
        0
      );

      setStats({
        totalInvestors: total,
        totalReceived,
        totalSent,
        totalBalance,
      });
    } catch (err) {
      // Only show error message for non-403 errors
      if (err.response?.status !== 403) {
        console.error("Error loading INR investors:", err);
        console.error("Error details:", err.response?.data);
        setError(
          `Failed to load INR investors: ${
            err.response?.data?.error || err.message
          }`
        );
      }
      // For 403, fail silently with empty data
      setInvestors([]);
      setTotal(0);
      setStats({
        totalInvestors: 0,
        totalReceived: 0,
        totalSent: 0,
        totalBalance: 0,
      });
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      setPage(1);
      loadInvestors({ search: search.trim(), page: 1 });
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(searchTimeout);
      // Clear search loading if component unmounts or search changes
      setSearchLoading(false);
    };
    // eslint-disable-next-line
  }, [search]);

  useEffect(() => {
    loadInvestors({ page: 1 });
    // eslint-disable-next-line
  }, []);

  const handleAddInvestor = async (data) => {
    setError(null);
    try {
      const newInvestor = await addINRInvestor(data);
      setShowModal(false);
      setPage(1);
      // Reload with current search context without showing main loading
      await loadInvestors({ search: search.trim(), page: 1 });
    } catch (err) {
      setError("Failed to add INR investor");
    }
  };

  const handleEditInvestor = async (data) => {
    setError(null);
    try {
      await updateINRInvestor(data.id, data);
      // Optimistically update the specific record in the current list
      const updatedInvestors = investors.map((inv) =>
        inv.id === data.id ? { ...inv, ...data } : inv
      );
      setInvestors(updatedInvestors);

      // Update statistics without full reload
      const totalReceived = updatedInvestors.reduce(
        (sum, inv) => sum + (parseFloat(inv.received) || 0),
        0
      );
      const totalSent = updatedInvestors.reduce(
        (sum, inv) => sum + (parseFloat(inv.sent) || 0),
        0
      );
      const totalBalance = updatedInvestors.reduce(
        (sum, inv) => sum + (parseFloat(inv.balance) || 0),
        0
      );

      setStats((prevStats) => ({
        ...prevStats,
        totalReceived,
        totalSent,
        totalBalance,
      }));
    } catch (err) {
      setError("Failed to update INR investor");
      // Reload on error to ensure data consistency
      loadInvestors({ search: search.trim(), page });
    }
  };

  const handleDeleteInvestor = async (inv) => {
    setRecordToDelete(inv);
    setShowDeleteModal(true);
  };

  const handleSoftDelete = async (deleteReason) => {
    if (!recordToDelete) return;

    setError(null);
    try {
      await softDeleteINRInvestor(recordToDelete.id, deleteReason);
      // Remove from current list
      const updatedInvestors = investors.filter(
        (item) => item.id !== recordToDelete.id
      );
      setInvestors(updatedInvestors);
      setTotal((prevTotal) => prevTotal - 1);

      // Update statistics
      const totalReceived = updatedInvestors.reduce(
        (sum, item) => sum + (parseFloat(item.received) || 0),
        0
      );
      const totalSent = updatedInvestors.reduce(
        (sum, item) => sum + (parseFloat(item.sent) || 0),
        0
      );
      const totalBalance = updatedInvestors.reduce(
        (sum, item) => sum + (parseFloat(item.balance) || 0),
        0
      );

      setStats({
        totalInvestors: total - 1,
        totalReceived,
        totalSent,
        totalBalance,
      });
    } catch (err) {
      setError("Failed to delete INR investor");
      console.error("Error deleting INR investor:", err);
    }
  };

  const handlePermanentDelete = async () => {
    if (!recordToDelete) return;

    setError(null);
    try {
      await deleteINRInvestor(recordToDelete.id);
      // Remove from current list
      const updatedInvestors = investors.filter(
        (item) => item.id !== recordToDelete.id
      );
      setInvestors(updatedInvestors);
      setTotal((prevTotal) => prevTotal - 1);

      // Update statistics
      const totalReceived = updatedInvestors.reduce(
        (sum, item) => sum + (parseFloat(item.received) || 0),
        0
      );
      const totalSent = updatedInvestors.reduce(
        (sum, item) => sum + (parseFloat(item.sent) || 0),
        0
      );
      const totalBalance = updatedInvestors.reduce(
        (sum, item) => sum + (parseFloat(item.balance) || 0),
        0
      );

      setStats({
        totalInvestors: total - 1,
        totalReceived,
        totalSent,
        totalBalance,
      });
    } catch (err) {
      setError("Failed to permanently delete INR investor");
      console.error("Error permanently deleting INR investor:", err);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadInvestors({ search: search.trim(), page: newPage });
  };

  const handleBulkUploadSuccess = () => {
    // Refresh the data after successful bulk upload
    loadInvestors({ search: search.trim(), page: 1 });
    setPage(1);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Enhanced Navigation Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <HomeIcon className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>•</span>
                <span>INR Investors Management</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/deleted-records"
                className="inline-flex items-center px-4 py-2 border border-purple-300 shadow-sm text-sm font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
              >
                <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                Deleted Records
              </Link>
              <Link
                to="/inr-investors-filter"
                className="inline-flex items-center px-4 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Advanced Filter
              </Link>
              <button
                onClick={() => setShowBulkUpload(!showBulkUpload)}
                className={`inline-flex items-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 ${
                  showBulkUpload
                    ? "border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                    : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                }`}
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                Bulk Upload
                {showBulkUpload ? (
                  <ChevronUpIcon className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 ml-2" />
                )}
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <PlusIcon className="h-5 w-5" />
                Add INR Investor
              </button>
              <div className="bg-green-50 px-3 py-1 rounded-full">
                <span className="text-sm font-medium text-green-700">
                  INR Management
                </span>
              </div>
            </div>
          </div>

          {/* Page Title and Description */}
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-2">
              <CurrencyDollarIcon className="h-8 w-8 text-gray-700" />
              <h1 className="text-3xl font-bold text-gray-900">
                INR Investors Management
              </h1>
            </div>
            <p className="text-gray-600 max-w-4xl">
              Manage and track all INR investor records, including transaction
              history, balances, and financial activities. Monitor received,
              sent amounts and current balances with live search functionality.
              Use bulk upload to import large datasets efficiently.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                💰 INR Transactions
              </span>
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                🔍 Live Search
              </span>
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                ⚡ Real-time Balance
              </span>
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                📊 Statistics Dashboard
              </span>
              <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
                📤 Bulk Upload
              </span>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <BanknotesIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Financial Overview
              </h3>
              <div className="ml-4 text-sm text-gray-500">
                Real-time INR transaction statistics
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-300 p-4 group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 bg-blue-100 border border-blue-200 rounded-lg flex items-center justify-center">
                    <UserGroupIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-900 mb-1">
                      {stats.totalInvestors.toLocaleString()}
                    </p>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Total Investors
                    </h3>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl shadow-sm hover:shadow-md hover:border-green-300 transition-all duration-300 p-4 group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 bg-green-100 border border-green-200 rounded-lg flex items-center justify-center">
                    <BanknotesIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-green-700 mb-1">
                      ₹
                      {stats.totalReceived.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Total Received
                    </h3>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl shadow-sm hover:shadow-md hover:border-red-300 transition-all duration-300 p-4 group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 bg-red-100 border border-red-200 rounded-lg flex items-center justify-center">
                    <BanknotesIcon className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-red-700 mb-1">
                      ₹
                      {stats.totalSent.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Total Sent
                    </h3>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-300 p-4 group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 bg-amber-100 border border-amber-200 rounded-lg flex items-center justify-center">
                    <CurrencyDollarIcon className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-xl font-bold mb-1 ${
                        stats.totalBalance < 0
                          ? "text-red-700"
                          : "text-amber-700"
                      }`}
                    >
                      ₹
                      {stats.totalBalance.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Total Balance
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Upload Section */}
        {showBulkUpload && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center mb-4">
                <ArrowUpTrayIcon className="h-5 w-5 text-gray-400 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Bulk Upload INR Investors
                </h3>
                <div className="ml-4 text-sm text-gray-500">
                  Upload multiple INR investor records at once
                </div>
              </div>
              <INRBulkUpload onUploadSuccess={handleBulkUploadSuccess} />
            </div>
          </div>
        )}

        {/* Enhanced Live Search Section */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Live Search</h3>
              <div className="ml-4 text-sm text-gray-500">
                Search across all INR investor fields
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search INR Investors
                  <span className="text-xs font-normal text-gray-500 ml-2">
                    (Results update as you type)
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name, phone, remarks, or added by..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                  {searchLoading && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
                {search && (
                  <p className="mt-2 text-xs text-gray-500">
                    {searchLoading ? (
                      "Searching..."
                    ) : (
                      <>
                        Showing {total} result{total !== 1 ? "s" : ""} for "
                        {search}"
                      </>
                    )}
                  </p>
                )}
              </div>
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Clear search"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-red-800 font-medium">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white shadow rounded-lg">
          {loading ? (
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600 text-lg">
                    Loading INR Investors...
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">
                    INR Investors Data
                  </h3>
                  <div className="ml-4 text-sm text-gray-500">
                    {search
                      ? `Filtered results for "${search}"`
                      : "All investor records"}
                  </div>
                </div>
                <INRInvestorsTable
                  investors={investors}
                  onEdit={handleEditInvestor}
                  onDelete={handleDeleteInvestor}
                />
              </div>

              {/* Enhanced Pagination */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
                    {Math.min(page * PAGE_SIZE, total)} of {total} records
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-sm font-medium text-gray-700">
                      Page {page} of {Math.ceil(total / PAGE_SIZE)}
                    </span>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page * PAGE_SIZE >= total}
                      className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Enhanced Modal for Add INR Investor */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Add INR Investor
                  </h2>
                  <button
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    onClick={() => setShowModal(false)}
                    aria-label="Close"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6">
                <INRInvestorForm onAdd={handleAddInvestor} />
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setRecordToDelete(null);
          }}
          onSoftDelete={handleSoftDelete}
          onPermanentDelete={handlePermanentDelete}
          itemName={recordToDelete?.name || "this record"}
          itemType="INR investor"
        />
      </div>
    </div>
  );
};

export default INRInvestorsPage;
